
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Eye, Image, Lightbulb, Sparkles, Star, User } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

// Sample data for inspiration gallery
const successStories = [
  {
    id: 1,
    title: "From Garage to Global: Tech Startup Journey",
    description: "How a small team turned their weekend project into a billion-dollar company.",
    image: "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d",
    category: "Success Story"
  },
  {
    id: 2,
    title: "Pivoting to Success: E-commerce Revolution",
    description: "When facing bankruptcy, this founder made one crucial decision that changed everything.",
    image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158",
    category: "Success Story"
  },
  {
    id: 3,
    title: "Sustainable Innovation in Manufacturing",
    description: "How eco-friendly practices became a competitive advantage for this industrial startup.",
    image: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6",
    category: "Industry Insight"
  },
  {
    id: 4,
    title: "Building in Public: Transparency as Strategy",
    description: "The story of founders who shared their journey openly and built a dedicated community.",
    image: "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b",
    category: "Expert Advice"
  },
  {
    id: 5,
    title: "Bootstrapping vs Funding: Making the Right Choice",
    description: "Real stories from founders who took different paths to growth.",
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475",
    category: "Expert Advice"
  }
];

// Featured content section
const FeaturedContent = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
      <Card className="md:col-span-2 overflow-hidden">
        <div className="md:grid md:grid-cols-2">
          <div className="relative h-48 md:h-full">
            <img 
              src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d" 
              alt="Featured story" 
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="p-6">
            <CardHeader className="p-0 pb-4">
              <div className="flex items-center mb-2">
                <Sparkles className="h-4 w-4 mr-2 text-primary" />
                <span className="text-sm font-medium text-primary">Featured Story</span>
              </div>
              <CardTitle className="text-2xl">Redefining Success: The Modern Founder's Journey</CardTitle>
            </CardHeader>
            <CardContent className="p-0 pb-4">
              <p className="text-muted-foreground">
                How today's entrepreneurs are balancing purpose and profit to build sustainable businesses 
                that make a difference while achieving financial success.
              </p>
            </CardContent>
            <CardFooter className="p-0 flex justify-between items-center">
              <Button variant="outline" className="gap-1">
                <Eye className="h-4 w-4" /> Read More
              </Button>
              <Button variant="ghost" size="icon">
                <Star className="h-4 w-4" />
              </Button>
            </CardFooter>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Content card component
const ContentCard = ({ item }: { item: typeof successStories[0] }) => {
  return (
    <Card className="overflow-hidden">
      <div className="relative h-48">
        <img 
          src={item.image} 
          alt={item.title} 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute top-2 right-2">
          <Button variant="secondary" size="icon" className="h-8 w-8 rounded-full opacity-90">
            <Star className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <CardHeader>
        <div className="flex items-center mb-1">
          {item.category === "Success Story" ? (
            <Sparkles className="h-4 w-4 mr-1 text-green-500" />
          ) : item.category === "Industry Insight" ? (
            <Lightbulb className="h-4 w-4 mr-1 text-blue-500" />
          ) : (
            <User className="h-4 w-4 mr-1 text-purple-500" />
          )}
          <span className="text-xs font-medium">{item.category}</span>
        </div>
        <CardTitle className="text-lg">{item.title}</CardTitle>
      </CardHeader>
      <CardContent>
        <CardDescription>
          {item.description}
        </CardDescription>
      </CardContent>
      <CardFooter>
        <Button variant="ghost" className="w-full gap-1">
          <Eye className="h-4 w-4" /> Read Full Story
        </Button>
      </CardFooter>
    </Card>
  );
};

const InspirationGallery = () => {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold tracking-tight">Inspiration Gallery</h2>
        <Button variant="outline" className="gap-1">
          <Image className="h-4 w-4" /> Vision Board
        </Button>
      </div>
      
      <FeaturedContent />
      
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-lg">Success Stories & Insights</h3>
          <Button variant="link" className="gap-1">
            View All
          </Button>
        </div>
        
        <ScrollArea className="pb-4">
          <div className="flex space-x-4 pb-4">
            {successStories.map((story) => (
              <div key={story.id} className="min-w-[300px] max-w-[300px]">
                <ContentCard item={story} />
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default InspirationGallery;
